"use client";

import { Carousel, CarouselApi, CarouselContent, CarouselItem, cn } from "@saridana-core/react-ui-kit";
import { ArticleCategory, PageData, TArticle } from "@shared/types";
import { ButtonLink } from "@shared/ui";
import { formatDate } from "@shared/utils";
import Image from "next/image";
import { useEffect, useState } from "react";

type Props = {
  items: PageData<TArticle>[];
  category: ArticleCategory;
};

export function ArticleSlideSection({ items, category }: Props) {
  const [api, setApi] = useState<CarouselApi>();

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  useEffect(() => {
    if (!api) return;

    setCurrentIndex(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrentIndex(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <section className="pt-[34px] sm:pt-[60px] relative overflow-hidden bg-white">
      <div className="lg:container relative">
        {items && items.length > 0 && (
          <div>
            <Carousel opts={{ align: "start", loop: true }} setApi={setApi}>
              <CarouselContent className="-mx-2 lg:-mx-8 xl:-mx-20">
                {items.map((article) => {
                  return (
                    <CarouselItem key={"article-" + article.attributes.slug + "-" + article.id} className="px-6 lg:px-8 xl:px-20">
                      <div className="flex flex-col lg:flex-row gap-6 md:gap-10 items-start h-full lg:h-auto">
                        <Image
                          src={article.attributes?.image?.data?.attributes?.url || ""}
                          alt={
                            article.attributes?.image?.data?.attributes?.alternativeText ||
                            article.attributes?.image?.data?.attributes?.name ||
                            ""
                          }
                          width={article.attributes?.image?.data?.attributes?.width || 760}
                          height={article.attributes?.image?.data?.attributes?.height || 428}
                          className="w-full lg:w-[50%] lg:min-w-[60%] h-[280px] sm:h-[320px] md:h-[428px] object-cover object-center bg-gray-100 rounded-xl"
                        />
                        <div className="flex-1 flex flex-col justify-between h-full">
                          <div>
                            <p className="text-lg font-medium text-gray-800">{formatDate(article.attributes?.publishedDate)}</p>
                            <h4 className="mt-4 sm:mt-6 font-bold text-[32px] sm:text-4xl md:text-5xl line-clamp-3 sm:line-clamp-2 lg:line-clamp-3 pb-3 sm:pb-4">
                              {article.attributes?.shortTitle}
                            </h4>
                            <p className="font-normal text-lg sm:text-xl line-clamp-3 sm:line-clamp-2 lg:line-clamp-3 text-gray-700">
                              {article.attributes?.fullTitle}
                            </p>
                          </div>
                          <div className="mt-8 sm:mt-9">
                            <ButtonLink
                              href={"/" + category + "/" + article.attributes?.slug}
                              variant="primary"
                              size="md"
                              target="_self"
                              text={article.attributes?.readMoreButtonText || "Baca Selengkapnya"}
                            />
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
            </Carousel>
          </div>
        )}
      </div>
      <div className="container mt-10 flex items-center gap-2.5">
        {items.map((_, index) => {
          return (
            <a
              key={"article-slide-indicator-" + index}
              className={cn(
                "inline-block rounded-full size-[14px] cursor-pointer",
                index === currentIndex ? "bg-primary-600" : "bg-gray-200",
              )}
              onClick={() => api?.scrollTo(index)}
            >
              &nbsp;
            </a>
          );
        })}
      </div>
    </section>
  );
}
